/**
 * 비밀번호 변경 모달 — 대시보드의 "비밀번호 변경" 버튼에서 호출.
 *
 * 과거 /me 페이지(MyAccountPage) 의 비번 변경 폼을 모달 형태로 옮긴 것.
 * 서버 규칙: 8자 이상 + 대/소문자 + 숫자 + 특수문자(!@#$%^&*())
 */
import React, { useMemo, useState } from "react";
// confirmDialog 의 알림 함수는 confirmNotify 로 이름이 바뀌었다(util/toast 의 notify 와 헷갈려서).
// 아래 본문이 confirmNotify 를 직접 부르므로 별칭 없이 그대로 들여온다.
import { confirmNotify } from "../../util/confirmDialog";
import {
    IconButton, InputAdornment, Stack,
} from "@mui/material";
import VisibilityOutlinedIcon    from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import { notify }  from "../../util/toast";
import { authApi } from "../auth/api/authApi";
import FormModal     from "../../component/common/FormModal";
// 한/영 상태를 모른 채 친 한글을 눌린 영문 키로 되돌린다 — 계정명 칸과 같은 규칙.
import PasswordField from "../../component/common/PasswordField";

// email 은 더 이상 받지 않는다 — 대상 계정을 서버가 토큰으로 확정하므로 화면이 알 필요가 없다.
export default function PasswordChangeModal({ open, onClose, onAfterChange }) {
    const [oldPw, setOldPw]   = useState("");
    const [newPw, setNewPw]   = useState("");
    const [newPw2, setNewPw2] = useState("");
    const [saving, setSaving] = useState(false);

    const [showOld, setShowOld]   = useState(false);
    const [showNew, setShowNew]   = useState(false);
    const [showNew2, setShowNew2] = useState(false);

    const reset = () => {
        setOldPw(""); setNewPw(""); setNewPw2("");
        setShowOld(false); setShowNew(false); setShowNew2(false);
    };

    const handleClose = () => {
        if (saving) return;
        reset();
        onClose?.();
    };

    const validateNewPassword = () => {
        if (!newPw || newPw.length < 8) return "비밀번호는 최소 8자 이상이어야 합니다.";
        if (!/[A-Z]/.test(newPw)) return "대문자를 포함해야 합니다.";
        if (!/[a-z]/.test(newPw)) return "소문자를 포함해야 합니다.";
        if (!/[0-9]/.test(newPw)) return "숫자를 포함해야 합니다.";
        if (!/[!@#$%^&*()]/.test(newPw)) return "특수문자(!@#$%^&*())를 포함해야 합니다.";
        if (newPw !== newPw2) return "새 비밀번호 확인이 일치하지 않습니다.";
        return null;
    };

    const canSubmit = useMemo(() => !!oldPw && !!newPw && !!newPw2, [oldPw, newPw, newPw2]);

    const handleSubmit = async () => {
        // 대상 계정은 서버가 토큰으로 확정한다(EmployeeController.changePassword: "email은 서버에서 확정").
        // 예전엔 여기서 email 이 없으면 막았는데, 서버가 쓰지도 않는 값 때문에
        // 내 정보 조회가 한 번 실패하면 비밀번호 변경이 통째로 잠겼다.
        if (!canSubmit) {
            notify("기존/새 비밀번호를 모두 입력하세요.", "warning");
            return;
        }
        const msg = validateNewPassword();
        if (msg) { notify(msg, "warning"); return; }

        setSaving(true);
        try {
            const res = await authApi.changePassword(oldPw, newPw);
            const message       = res?.message || "비밀번호가 변경되었습니다.";
            const requireLogout = !!res?.requireLogout;

            reset();
            onClose?.();

            if (requireLogout) {
                // 재로그인이 필요한 경우 곧바로 window.location.replace 로 페이지가 갈아엎히므로
                // 토스트는 뜨자마자 사라진다. 사용자가 "확인" 을 누를 때까지 멈추는 모달로 안내.
                await confirmNotify({ message, severity: "success" });
                onAfterChange?.({ requireLogout: true });
            } else {
                notify(message, "success");
                onAfterChange?.({ requireLogout: false });
            }
        } catch (e) {
            notify(e?.response?.data?.message || "비밀번호 변경 실패", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <FormModal
            open={open}
            onClose={handleClose}
            title="비밀번호 변경"
            subtitle="8자 이상 · 대문자/소문자/숫자/특수문자(!@#$%^&*()) 포함"
            maxWidth="xs"
            onSave={handleSubmit}
            saving={saving}
            saveLabel={saving ? "변경 중…" : "변경"}
            saveDisabled={!canSubmit}
        >
            <Stack spacing={1.25}>
                <PasswordField
                    label="기존 비밀번호" size="small"
                    type={showOld ? "text" : "password"}
                    value={oldPw} onChange={setOldPw}
                    autoComplete="current-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowOld(v => !v)} edge="end">
                                    {showOld ? <VisibilityOutlinedIcon fontSize="small" /> : <VisibilityOffOutlinedIcon fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }} />
                <PasswordField
                    label="새 비밀번호" size="small"
                    type={showNew ? "text" : "password"}
                    value={newPw} onChange={setNewPw}
                    autoComplete="new-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowNew(v => !v)} edge="end">
                                    {showNew ? <VisibilityOutlinedIcon fontSize="small" /> : <VisibilityOffOutlinedIcon fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }} />
                <PasswordField
                    label="새 비밀번호 확인" size="small"
                    type={showNew2 ? "text" : "password"}
                    value={newPw2} onChange={setNewPw2}
                    autoComplete="new-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowNew2(v => !v)} edge="end">
                                    {showNew2 ? <VisibilityOutlinedIcon fontSize="small" /> : <VisibilityOffOutlinedIcon fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }} />
            </Stack>
        </FormModal>
    );
}
